import React, { useState, useEffect } from 'react';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { GraduationCap, X } from 'lucide-react';
import { createTPOOnboarding, uploadFile } from '../../services/usersapi';
import { useNavigate } from 'react-router-dom';
import { useRbac } from '../../context'; // <-- Import your context here

const TPOOnboardingForm: React.FC = () => {
  type CollegeOption = { value: string; label: string };

  const navigate = useNavigate();
  const { setUser, user } = useRbac(); // <-- Import/use this from your context
  const FORM_STORAGE_KEY = 'tpoOnboardingFormData';

  // Initial form data with localStorage fallback
  const getInitialFormData = () => {
    try {
      const savedData = localStorage.getItem(FORM_STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        // Ensure collegeName is an array (in case of localStorage corruption)
        if (parsedData.collegeName && Array.isArray(parsedData.collegeName)) {
          return parsedData;
        }
      }
    } catch (error) {
      console.error('Error loading form data from localStorage:', error);
    }
    
    // Return default form data if localStorage is empty or corrupted
    return {
      tpoId: '',
      fullName: '',
      designation: '',
      department: '',
      collegeName: [],
      collegeCode: '',
      officialEmail: '',
      alternateEmail: '',
      mobileNumber: '',
      alternateNumber: '',
      whatsappNumber: '',
      linkedinProfile: '',
      officeAddress: '',
      city: '',
      state: '',
      pincode: '',
      yearsOfExperience: '',
      specialization: '',
      preferredCommunication: '',
      officeHours: '',
      reportingPersonName: '',
      reportingDesignation: '',
      reportingMobile: '',
      reportingEmail: '',
      principalName: '',
      principalDesignation: '',
      principalMobile: '',
      principalEmail: '',
      collegeLogo: null,
      agreeTermsAndConditions: false,
      agreeAgreement: false
    };
  };

  const [formData, setFormData] = useState<{
    tpoId: string;
    fullName: string;
    designation: string;
    department: string;
    collegeName: CollegeOption[];
    collegeCode: string;
    officialEmail: string;
    alternateEmail: string;
    mobileNumber: string;
    alternateNumber: string;
    whatsappNumber: string;
    linkedinProfile: string;
    officeAddress: string;
    city: string;
    state: string;
    pincode: string;
    yearsOfExperience: string;
    specialization: string;
    preferredCommunication: string;
    officeHours: string;
    reportingPersonName: string;
    reportingDesignation: string;
    reportingMobile: string;
    reportingEmail: string;
    principalName: string;
    principalDesignation: string;
    principalMobile: string;
    principalEmail: string;
    collegeLogo: File | null;
    agreeTermsAndConditions: boolean;
    agreeAgreement: boolean;

  }>(getInitialFormData);

  // Add loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);


  // Save form data to localStorage whenever formData changes
  useEffect(() => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error('Error saving form data to localStorage:', error);
    }
  }, [formData]);

  // Clear localStorage on successful submission
  const clearFormStorage = () => {
    try {
      localStorage.removeItem(FORM_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing form data from localStorage:', error);
    }
  };

  // Add college options
  const collegeOptions = [
    { value: 'iit-delhi', label: 'Indian Institute of Technology Delhi' },
    { value: 'iit-bombay', label: 'Indian Institute of Technology Bombay' },
    { value: 'iit-kanpur', label: 'Indian Institute of Technology Kanpur' },
    { value: 'iit-kharagpur', label: 'Indian Institute of Technology Kharagpur' },
    { value: 'iit-madras', label: 'Indian Institute of Technology Madras' },
    { value: 'iisc-bangalore', label: 'Indian Institute of Science Bangalore' },
    { value: 'jnu', label: 'Jawaharlal Nehru University' },
    { value: 'delhi-university', label: 'University of Delhi' },
    { value: 'jamia-millia', label: 'Jamia Millia Islamia' },
    { value: 'amu', label: 'Aligarh Muslim University' },
    { value: 'bhu', label: 'Banaras Hindu University' },
    { value: 'anna-university', label: 'Anna University' },
    { value: 'mumbai-university', label: 'University of Mumbai' },
    { value: 'pune-university', label: 'University of Pune' },
    { value: 'calcutta-university', label: 'University of Calcutta' },
    { value: 'manipal', label: 'Manipal Academy of Higher Education' },
    { value: 'srm', label: 'SRM Institute of Science and Technology' },
    { value: 'vit', label: 'VIT University' },
    { value: 'amity', label: 'Amity University' },
    { value: 'lpu', label: 'Lovely Professional University' },
    { value: 'other', label: 'Other' }
  ];

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validatePincode = (pincode: string): boolean => {
    const pincodeRegex = /^\d{6}$/;
    return pincodeRegex.test(pincode);
  };

  const validateLinkedIn = (url: string): boolean => {
    if (!url) return true; // LinkedIn is optional
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/;
    return linkedinRegex.test(url);
  };



  const validateLogo = (file: File | null): boolean => {
    if (!file) return false; // Logo is now required
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    return validTypes.includes(file.type) && file.size <= maxSize;
  };


  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Required field validations
    if (!formData.tpoId.trim()) errors.tpoId = 'TPO ID is required';
    if (!formData.fullName.trim()) errors.fullName = 'Full Name is required';
    if (!formData.designation) errors.designation = 'Designation is required';
    if (!formData.department.trim()) errors.department = 'Department is required';
    if (formData.collegeName.length === 0) errors.collegeName = 'At least one college must be selected';
    if (!formData.collegeCode.trim()) errors.collegeCode = 'College Code is required';
    if (!formData.officialEmail.trim()) errors.officialEmail = 'Official Email is required';
    if (!formData.mobileNumber.trim()) errors.mobileNumber = 'Mobile Number is required';
    if (!formData.officeAddress.trim()) errors.officeAddress = 'Office Address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.pincode.trim()) errors.pincode = 'Pincode is required';
    if (!formData.preferredCommunication) errors.preferredCommunication = 'Preferred Communication Mode is required';
    if (!formData.officeHours.trim()) errors.officeHours = 'Office Hours is required';

    // Email validations
    if (formData.officialEmail && !validateEmail(formData.officialEmail)) {
      errors.officialEmail = 'Please enter a valid email address';
    }
    if (formData.alternateEmail && !validateEmail(formData.alternateEmail)) {
      errors.alternateEmail = 'Please enter a valid email address';
    }
    if (formData.reportingEmail && !validateEmail(formData.reportingEmail)) {
      errors.reportingEmail = 'Please enter a valid email address';
    }
    if (formData.principalEmail && !validateEmail(formData.principalEmail)) {
      errors.principalEmail = 'Please enter a valid email address';
    }

    // Phone number validations
    if (formData.mobileNumber && !validatePhone(formData.mobileNumber)) {
      errors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }
    if (formData.alternateNumber && !validatePhone(formData.alternateNumber)) {
      errors.alternateNumber = 'Please enter a valid 10-digit mobile number';
    }
    if (formData.whatsappNumber && !validatePhone(formData.whatsappNumber)) {
      errors.whatsappNumber = 'Please enter a valid 10-digit WhatsApp number';
    }
    if (formData.reportingMobile && !validatePhone(formData.reportingMobile)) {
      errors.reportingMobile = 'Please enter a valid 10-digit mobile number';
    }
    if (formData.principalMobile && !validatePhone(formData.principalMobile)) {
      errors.principalMobile = 'Please enter a valid 10-digit mobile number';
    }

    // Pincode validation
    if (formData.pincode && !validatePincode(formData.pincode)) {
      errors.pincode = 'Please enter a valid 6-digit pincode';
    }

    // LinkedIn validation
    if (formData.linkedinProfile && !validateLinkedIn(formData.linkedinProfile)) {
      errors.linkedinProfile = 'Please enter a valid LinkedIn URL';
    }

    // Logo validation
    if (!formData.collegeLogo) {
      errors.collegeLogo = 'College/Company Logo is required';
    } else if (!validateLogo(formData.collegeLogo)) {
      errors.collegeLogo = 'Please upload a valid logo (PNG/JPG, max 5MB)';
    }

    // Checkbox validations
    if (!formData.agreeTermsAndConditions) {
      errors.agreeTermsAndConditions = 'You must agree to the Terms and Conditions';
    }
    if (!formData.agreeAgreement) {
      errors.agreeAgreement = 'You must agree to the Agreement';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: '' });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
    
    // Clear validation error when user checks/unchecks
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: '' });
    }
  };

  const handleCollegeSelect = (value: string) => {
    const selectedCollege = collegeOptions.find(option => option.value === value);
    if (selectedCollege && !formData.collegeName.some(college => college.value === value)) {
      setFormData({
        ...formData,
        collegeName: [...formData.collegeName, selectedCollege]
      });
      
      // Clear validation error for college selection
      if (validationErrors.collegeName) {
        setValidationErrors({ ...validationErrors, collegeName: '' });
      }
    }
  };

  const removeCollege = (valueToRemove: string) => {
    setFormData({
      ...formData,
      collegeName: formData.collegeName.filter(college => college.value !== valueToRemove)
    });
  };

  // Clear form function
  const clearForm = () => {
    const emptyFormData = {
      tpoId: '',
      fullName: '',
      designation: '',
      department: '',
      collegeName: [],
      collegeCode: '',
      officialEmail: '',
      alternateEmail: '',
      mobileNumber: '',
      alternateNumber: '',
      whatsappNumber: '',
      linkedinProfile: '',
      officeAddress: '',
      city: '',
      state: '',
      pincode: '',
      yearsOfExperience: '',
      specialization: '',
      preferredCommunication: '',
      officeHours: '',
      reportingPersonName: '',
      reportingDesignation: '',
      reportingMobile: '',
      reportingEmail: '',
      principalName: '',
      principalDesignation: '',
      principalMobile: '',
      principalEmail: '',
      collegeLogo: null,
      agreeTermsAndConditions: false,
      agreeAgreement: false 


    };
    
    setFormData(emptyFormData);
    setValidationErrors({});
    setError(null);
    setUploadedLogoUrl(null);
    setLogoPreview(null);
    clearFormStorage();
  };



  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData({ ...formData, collegeLogo: file });
    
    if (file && validateLogo(file)) {
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
      
      // Clear validation error
      if (validationErrors.collegeLogo) {
        setValidationErrors({ ...validationErrors, collegeLogo: '' });
      }

      // Upload file to server
      setIsUploadingLogo(true);
      try {
        const uploadResponse = await uploadFile(file, 'tpo-logos');
        setUploadedLogoUrl(uploadResponse.fileUrl);
        console.log('Logo uploaded successfully:', uploadResponse.fileUrl);
      } catch (error) {
        console.error('Failed to upload logo:', error);
        // Keep the file in formData but show error
        setError('Failed to upload logo. Please try again.');
      } finally {
        setIsUploadingLogo(false);
      }
    } else {
      setLogoPreview(null);
      setUploadedLogoUrl(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    // Additional check for logo upload
    if (!uploadedLogoUrl) {
      setError('Please wait for the logo to finish uploading before submitting');
      return;
    }

    setIsLoading(true);
    setIsSubmitting(true);
    setError(null);

    try {
      // Transform form data to match API structure
      const apiData = {
        tpoId: formData.tpoId,
        fullName: formData.fullName,
        designation: formData.designation,
        department: formData.department,
        selectedColleges: formData.collegeName.map(college => college.label),
        collegeCode: formData.collegeCode,
        collegeLogo: uploadedLogoUrl, // Include the uploaded logo URL
        
        workDetails: {
          preferredCommunicationMode: formData.preferredCommunication,
          officeHours: formData.officeHours
        },

        reportingPerson: {
          name: formData.reportingPersonName,
          designation: formData.reportingDesignation,
          mobile: formData.reportingMobile,
          email: formData.reportingEmail
        },

        principalDirector: {
          name: formData.principalName,
          designation: formData.principalDesignation,
          mobile: formData.principalMobile,
          email: formData.principalEmail
        },

        contactInformation: {
          officialEmail: formData.officialEmail,
          alternateEmail: formData.alternateEmail,
          mobile: formData.mobileNumber,
          alternateMobile: formData.alternateNumber,
          whatsapp: formData.whatsappNumber,
          linkedin: formData.linkedinProfile
        },

        collegeAddress: {
          officeAddress: formData.officeAddress,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        }
      };

      const response = await createTPOOnboarding(apiData);
      console.log('TPO Onboarding successful:', response);

      // Update user context to mark as onboarded
      if (setUser && user) {
        setUser({ ...user, isOnboarded: true });
      }

      clearFormStorage();
      setError(null);

      setTimeout(() => {
        navigate('/TalentPool');
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }, 1000);

    } catch (error: any) {
      console.error('TPO Onboarding failed:', error);
      setError(error.message || 'Failed to submit TPO onboarding');
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const renderFieldError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      return <p className="text-red-500 text-sm mt-1">{validationErrors[fieldName]}</p>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white p-6 w-full">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Welcome to EarlyJobs</h1>
            <p className="text-gray-600">Complete your profile to get started</p>
          </div>
          
       
        </div>
        
       
        
        {/* Error/Success message display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Success message when submitting */}
        {isSubmitting && !error && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Form submitted successfully! Redirecting...
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full">
          <div className="bg-white p-6 rounded-lg">


          <h2 className="text-xl font-semibold text-orange-500 mb-4">Company Logo</h2>
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">Upload College/Company Logo <span className="text-red-500">*</span> (PNG/JPG, max 5MB)</label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleLogoChange}
                  disabled={isSubmitting || isUploadingLogo}
                  className={`w-full p-2 border rounded ${validationErrors.collegeLogo ? 'border-red-500' : ''} ${isSubmitting || isUploadingLogo ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {isUploadingLogo && (
                  <div className="flex items-center text-blue-600 text-sm mt-2">
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Uploading logo...
                  </div>
                )}
                {logoPreview && (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      className="h-16 w-16 object-contain rounded"
                    />
                    {!isSubmitting && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, collegeLogo: null });
                          setLogoPreview(null);
                          setUploadedLogoUrl(null);
                        }}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {renderFieldError('collegeLogo')}
            </div>






            <h2 className="text-xl font-semibold text-orange-500 mb-4">Personal Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700">TPO ID (unique identifier) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="tpoId"
                  value={formData.tpoId}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.tpoId ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('tpoId')}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className={`w-full p-2 border rounded ${validationErrors.fullName ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {renderFieldError('fullName')}
                </div>
                <div>
                  <label className="block text-gray-700">Designation <span className="text-red-500">*</span></label>
                  <select
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className={`w-full p-2 border rounded ${validationErrors.designation ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select</option>
                    <option value="Training & Placement Officer">Training & Placement Officer</option>
                    <option value="Assistant TPO">Assistant TPO</option>
                  </select>
                  {renderFieldError('designation')}
                </div>
              </div>
              <div>
                <label className="block text-gray-700">Department <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.department ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('department')}
              </div>
              <div>
                <label className="block text-gray-700 mb-2">College/University Name <span className="text-red-500">*</span></label>
                <SearchableSelect
                  options={collegeOptions}
                  value=""
                  onValueChange={handleCollegeSelect}
                  placeholder="Select College/University"
                  emptyText="No colleges found"
                  searchPlaceholder="Search colleges..."
                  className={`w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 ${validationErrors.collegeName ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  icon={<GraduationCap className="h-4 w-4 text-gray-500" />}
                  searchable
                  disabled={isSubmitting}
                />
                {renderFieldError('collegeName')}
                
                {/* Display selected colleges */}
                {formData.collegeName.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Selected Colleges:</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.collegeName.map((college) => (
                        <div
                          key={college.value}
                          className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full flex items-center gap-2 text-sm"
                        >
                          <span>{college.label}</span>
                          {!isSubmitting && (
                            <button
                              type="button"
                              onClick={() => removeCollege(college.value)}
                              className="hover:bg-orange-200 rounded-full p-1"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-gray-700">College Code / Registration Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="collegeCode"
                  value={formData.collegeCode}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.collegeCode ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('collegeCode')}
              </div>
            </div>
            <h2 className="text-xl font-semibold text-orange-500 mt-6 mb-4">Contact Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700">Official Email ID <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="officialEmail"
                  value={formData.officialEmail}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.officialEmail ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('officialEmail')}
              </div>
              <div>
                <label className="block text-gray-700">Alternate Email ID</label>
                <input
                  type="email"
                  name="alternateEmail"
                  value={formData.alternateEmail}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.alternateEmail ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('alternateEmail')}
              </div>
              <div>
                <label className="block text-gray-700">Mobile Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.mobileNumber ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('mobileNumber')}
              </div>
              <div>
                <label className="block text-gray-700">Alternate Number</label>
                <input
                  type="tel"
                  name="alternateNumber"
                  value={formData.alternateNumber}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.alternateNumber ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('alternateNumber')}
              </div>
              <div>
                <label className="block text-gray-700">WhatsApp Number</label>
                <input
                  type="tel"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.whatsappNumber ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('whatsappNumber')}
              </div>
              <div>
                <label className="block text-gray-700">LinkedIn Profile (optional)</label>
                <input
                  type="url"
                  name="linkedinProfile"
                  value={formData.linkedinProfile}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.linkedinProfile ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
                {renderFieldError('linkedinProfile')}
              </div>
            </div>
            <h2 className="text-xl font-semibold text-orange-500 mt-6 mb-4">College Address</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700">Office Address <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="officeAddress"
                  value={formData.officeAddress}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.officeAddress ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('officeAddress')}
              </div>
              <div>
                <label className="block text-gray-700">City <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.city ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('city')}
              </div>
              <div>
                <label className="block text-gray-700">State <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.state ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('state')}
              </div>
              <div>
                <label className="block text-gray-700">Pincode <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.pincode ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('pincode')}
              </div>
            </div>
            <h2 className="text-xl font-semibold text-orange-500 mt-6 mb-4">Work Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700">Preferred Communication Mode <span className="text-red-500">*</span></label>
                <select
                  name="preferredCommunication"
                  value={formData.preferredCommunication}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.preferredCommunication ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select</option>
                  <option value="Email">Email</option>
                  <option value="Call">Call</option>
                  <option value="WhatsApp">WhatsApp</option>
                </select>
                {renderFieldError('preferredCommunication')}
              </div>
              <div>
                <label className="block text-gray-700">Office Hours / Availability <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="officeHours"
                  value={formData.officeHours}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.officeHours ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="e.g., 9:00 AM - 5:00 PM"
                />
                {renderFieldError('officeHours')}
              </div>
            </div>
            <h2 className="text-xl font-semibold text-orange-500 mt-6 mb-4">Reporting Person Contact</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700">Reporting Person Name</label>
                <input
                  type="text"
                  name="reportingPersonName"
                  value={formData.reportingPersonName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <label className="block text-gray-700">Designation</label>
                <input
                  type="text"
                  name="reportingDesignation"
                  value={formData.reportingDesignation}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <label className="block text-gray-700">Mobile Number</label>
                <input
                  type="tel"
                  name="reportingMobile"
                  value={formData.reportingMobile}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.reportingMobile ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('reportingMobile')}
              </div>
              <div>
                <label className="block text-gray-700">Email ID</label>
                <input
                  type="email"
                  name="reportingEmail"
                  value={formData.reportingEmail}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.reportingEmail ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('reportingEmail')}
              </div>
            </div>
            <h2 className="text-xl font-semibold text-orange-500 mt-6 mb-4">Principal/Director Contact</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700">Principal/Director Name</label>
                <input
                  type="text"
                  name="principalName"
                  value={formData.principalName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <label className="block text-gray-700">Designation</label>
                <input
                  type="text"
                  name="principalDesignation"
                  value={formData.principalDesignation}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <label className="block text-gray-700">Mobile Number</label>
                <input
                  type="tel"
                  name="principalMobile"
                  value={formData.principalMobile}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.principalMobile ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('principalMobile')}
              </div>
              <div>
                <label className="block text-gray-700">Email ID</label>
                <input
                  type="email"
                  name="principalEmail"
                  value={formData.principalEmail}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`w-full p-2 border rounded ${validationErrors.principalEmail ? 'border-red-500' : ''} ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {renderFieldError('principalEmail')}
              </div>
            </div>
            
            {/* Terms and Conditions Checkboxes */}
            <div className="mt-6 space-y-4">
              <h2 className="text-xl font-semibold text-orange-500 mb-4">Agreements</h2>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="agreeTermsAndConditions"
                    name="agreeTermsAndConditions"
                    checked={formData.agreeTermsAndConditions}
                    onChange={handleCheckboxChange}
                    disabled={isSubmitting}
                    className={`mt-1 h-4 w-4 accent-orange-600 border-gray-300 rounded focus:ring-orange-500 ${validationErrors.agreeTermsAndConditions ? 'border-red-500' : ''} ${isSubmitting ? 'cursor-not-allowed' : ''}`}
                  />
                  <label htmlFor="agreeTermsAndConditions" className={`text-sm text-gray-700 ${isSubmitting ? 'text-gray-400' : ''}`}>
                    I agree to the <a href="https://www.earlyjobs.ai/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-orange-600 font-medium hover:text-orange-700 underline">Terms and Conditions</a> <span className="text-red-500">*</span>
                  </label>
                </div>
                {renderFieldError('agreeTermsAndConditions')}
                
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="agreeAgreement"
                    name="agreeAgreement"
                    checked={formData.agreeAgreement}
                    onChange={handleCheckboxChange}
                    disabled={isSubmitting}
                    className={`mt-1 h-4 w-4 accent-orange-600 border-gray-300 rounded focus:ring-orange-500 ${validationErrors.agreeAgreement ? 'border-red-500' : ''} ${isSubmitting ? 'cursor-not-allowed' : ''}`}
                  />
                  <label htmlFor="agreeAgreement" className={`text-sm text-gray-700 ${isSubmitting ? 'text-gray-400' : ''}`}>
                    I agree to the <span 
                      className="text-orange-600 font-medium hover:text-orange-700 underline cursor-pointer" 
                      onClick={() => window.open('/agreement-document/sample', '_blank', 'noopener,noreferrer')}
                    >
                      Agreement
                    </span> <span className="text-red-500">*</span>
                  </label>
                </div>
                {renderFieldError('agreeAgreement')}
              </div>
            </div>
            
            {/* Submit and Clear buttons */}
            <div className="flex justify-between items-center mt-6">
              <button
                type="submit"
                disabled={isLoading || isSubmitting || isUploadingLogo}
                className={`px-6 py-3 rounded text-white font-medium transition-all duration-300 ${
                  isLoading || isSubmitting || isUploadingLogo
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-orange-500 hover:bg-orange-600 hover:shadow-lg'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-3 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Processing...
                  </div>
                ) : isLoading ? (
                  'Submitting...'
                ) : (
                  'Submit & Continue'
                )}
              </button>
              
              {/* <div className="text-sm text-gray-500">
                {Object.keys(formData).some(key => formData[key as keyof typeof formData] !== '' && 
                  !(Array.isArray(formData[key as keyof typeof formData]) && formData[key as keyof typeof formData].length === 0)) && (
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    Draft saved
                  </span>
                )}
              </div> */}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TPOOnboardingForm;